import type {
  IGiphyResponse,
  ILogoFinderResponse,
  IMediaFile,
  IMediaUploadData,
  IMediaUploadResponse,
  IPexelVideosResponse,
  IUnsplashImagesResponse,
} from "@/shared/types";
import { api } from "./api";

const path = (path: string) => `/media${path}`;

export const mediaApi = api.injectEndpoints({
  endpoints: (builder) => ({
    uploadMedia: builder.mutation<IMediaUploadResponse, IMediaUploadData>({
      query: ({ signal, ...payload }) => ({
        url: path(""),
        method: "POST",
        signal,
        body: payload,
      }),
    }),
    searchImages: builder.mutation<
      IUnsplashImagesResponse,
      {
        page?: number;
        per_page?: number;
        query?: string;
        /** Passed through to Unsplash; slide picture slots ask for landscape. */
        orientation?: "landscape" | "portrait" | "squarish";
        signal?: AbortSignal;
      }
    >({
      query: ({ page = 1, per_page = 30, query = "Team", orientation, signal }) => ({
        url: path("/unsplash"),
        method: "POST",
        body: { page, per_page, query, ...(orientation ? { orientation } : {}) },
        // The caller passes one and a slide build aborts on Stop; discarded
        // here, the request ran on to its own timeout after the user had
        // already left. Forwarded the same way src/api/ai.ts does.
        signal,
      }),
    }),
    searchVideos: builder.mutation<
      IPexelVideosResponse,
      {
        page?: number;
        per_page?: number;
        query?: string;
        signal?: AbortSignal;
      }
    >({
      query: ({ page = 1, per_page = 30, query = "Team", signal }) => ({
        url: path("/pexels"),
        method: "POST",
        signal,
        body: { page, per_page, query },
      }),
    }),
    getUrlMetadata: builder.query<
      any,
      {
        url: string;
        type?: string;
        signal?: AbortSignal;
      }
    >({
      query: ({ url, type, signal }) => ({
        url: path("/url-metadata"),
        method: "GET",
        signal,
        params: { url, type },
      }),
    }),
    searchBrands: builder.mutation({
      query: ({
        domain,
        disableRateLimit,
      }: {
        domain: string;
        disableRateLimit: boolean;
      }) => ({
        url: "/brandfetch",
        method: "POST",
        body: { domain, disableRateLimit },
      }),
    }),
    searchBrandsLogos: builder.mutation<
      ILogoFinderResponse,
      {
        domain: string;
        disableRateLimit?: boolean;
        signal?: AbortSignal;
      }
    >({
      query: ({ domain, signal }) => ({
        url: "/brandfetch/logo-finder",
        method: "POST",
        signal,
        body: { domain },
      }),
    }),
    editImageStyle: builder.mutation({
      query: ({
        url,
        options,
      }: {
        url: string;
        options: { brightness: boolean; invert: boolean };
      }) => ({
        url: path("/image-style"),
        method: "POST",
        body: { url, options },
      }),
    }),
    getConvertApiToken: builder.query({
      query: () => ({
        url: path("/convertApiToken"),
        method: "GET",
      }),
    }),
    searchGifs: builder.mutation<
      IGiphyResponse,
      {
        query: string;
        limit?: number;
        offset?: number;
        bundle?: string;
        signal?: AbortSignal;
      }
    >({
      query: ({ signal, ...params }) => ({
        url: path("/giphy"),
        method: "GET",
        signal,
        params,
      }),
    }),
    getMediaFileById: builder.query<IMediaFile | null, string>({
      query: (id) => ({
        url: path(`/${id}`),
        method: "GET",
      }),
    }),
    getMediaByHash: builder.query<IMediaFile | null, string>({
      query: (hash) => ({
        url: path("/by-hash"),
        method: "GET",
        params: { hash },
      }),
    }),
  }),
});

export const {
  useSearchImagesMutation,
  useSearchVideosMutation,
  useUploadMediaMutation,
  useLazyGetUrlMetadataQuery,
  useSearchBrandsMutation,
  useEditImageStyleMutation,
  useLazyGetConvertApiTokenQuery,
  useSearchBrandsLogosMutation,
  useSearchGifsMutation,
  useLazyGetMediaFileByIdQuery,
  useLazyGetMediaByHashQuery,
} = mediaApi;
